/** Split stored rest duration (seconds) into minute/second parts. */
export function splitRestTimeSeconds(totalSeconds) {
  if (
    totalSeconds === null ||
    totalSeconds === undefined ||
    totalSeconds === ""
  ) {
    return { minutes: "", seconds: "" };
  }
  const n = Number(totalSeconds);
  if (!Number.isFinite(n) || n <= 0) {
    return { minutes: "", seconds: "" };
  }
  const whole = Math.floor(n);
  return {
    minutes: Math.floor(whole / 60),
    seconds: whole % 60,
  };
}

/** Format seconds as min:sec for text input (e.g. 90 → "1:30"). */
export function formatRestTimeInput(totalSeconds) {
  if (
    totalSeconds === null ||
    totalSeconds === undefined ||
    totalSeconds === ""
  ) {
    return "";
  }
  const n = Number(totalSeconds);
  if (!Number.isFinite(n) || n <= 0) return "";
  const whole = Math.floor(n);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Parse min:sec text (e.g. "2:30", "1:05") or plain seconds (e.g. "90").
 * Returns total seconds or null if empty/invalid.
 */
export function parseRestTimeInput(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (trimmed.includes(":")) {
    const parts = trimmed.split(":");
    if (parts.length !== 2) return null;
    const m = parseInt(parts[0], 10);
    const s = parseInt(parts[1], 10);
    if (
      !Number.isFinite(m) ||
      !Number.isFinite(s) ||
      m < 0 ||
      s < 0 ||
      s >= 60
    ) {
      return null;
    }
    const total = m * 60 + s;
    return total > 0 ? total : null;
  }

  const n = parseInt(trimmed, 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

/** Combine min/sec form fields into total seconds for API storage. */
export function combineRestTimeParts(minutes, seconds) {
  const parsePart = (v) => {
    if (v === "" || v === null || v === undefined) return 0;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };
  let m = parsePart(minutes);
  let s = parsePart(seconds);
  m += Math.floor(s / 60);
  s %= 60;
  const total = m * 60 + s;
  return total > 0 ? total : null;
}

/** Human-readable rest label (e.g. "2 min 30 sec"). */
export function formatRestTime(seconds) {
  if (seconds === null || seconds === undefined || seconds === "") return "";
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return "";
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  if (m > 0 && s > 0) return `${m} min ${s} sec`;
  if (m > 0) return `${m} min`;
  return `${s} sec`;
}

/** Add restTimeInput (min:sec string) for react-final-form. */
export function withRestTimeFormFields(exercise) {
  return {
    ...exercise,
    restTimeInput: formatRestTimeInput(exercise?.restTime),
  };
}

/** Read rest duration from form exercise row. */
export function restTimeFromFormExercise(exercise) {
  if (!exercise) return null;
  if (exercise.restTimeInput !== undefined) {
    return parseRestTimeInput(exercise.restTimeInput);
  }
  if (
    exercise.restTimeMinutes !== undefined ||
    exercise.restTimeSeconds !== undefined
  ) {
    return combineRestTimeParts(
      exercise.restTimeMinutes,
      exercise.restTimeSeconds
    );
  }
  if (
    exercise.restTime === "" ||
    exercise.restTime === null ||
    exercise.restTime === undefined
  ) {
    return null;
  }
  const n = parseInt(exercise.restTime, 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Normalize user input to M:SS on blur when parseable. */
export function normalizeRestTimeInput(value) {
  const seconds = parseRestTimeInput(value);
  if (seconds === null) return value === undefined || value === null ? "" : String(value).trim();
  return formatRestTimeInput(seconds);
}
